// Axios instance with interceptors

import axios, { AxiosError } from 'axios';
import { config, API_TIMEOUT } from '@/config/constants';
import { isTokenExpired, isTokenExpiringSoon, getTokenTimeRemaining } from '@/utils/jwt';
import TokenStorage from '@/utils/tokenStorage';
import { logger } from '@/utils/logger';
import { reportClientError } from '@/utils/clientErrorReporter';
import { performanceTracingService } from './performanceTracingService';

// Create axios instance lazy (solo se crea cuando se accede)
// IMPORTANTE: La instancia se crea de forma lazy para evitar evaluación inmediata de config.API_BASE_URL
// durante la inicialización del módulo. Esto permite que la aplicación cargue incluso si faltan variables de entorno.
let _apiInstance: ReturnType<typeof axios.create> | null = null;

const getApiInstance = () => {
  if (_apiInstance === null) {
    _apiInstance = axios.create({
      baseURL: config.API_BASE_URL, // Se evalúa solo cuando se crea la instancia
      timeout: API_TIMEOUT,
      withCredentials: true, // Enviar cookies httpOnly cross-origin (sesión persistente crm.migro.es ↔ api.migro.es)
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });
    
    // Configurar interceptores después de crear la instancia
    setupApiInterceptors(_apiInstance);
  }
  return _apiInstance;
};

// Exportar como getter para evaluación lazy
export const api = new Proxy({} as ReturnType<typeof axios.create>, {
  get(_target, prop) {
    return getApiInstance()[prop as keyof ReturnType<typeof axios.create>];
  },
  set(_target, prop, value) {
    (getApiInstance() as any)[prop] = value;
    return true;
  },
});

// --- Refresh token (compartido por interceptores Axios y WebSocket transcripción) ---
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Refresca el token de acceso proactivamente
 * @returns Promise con el nuevo token o null si falla
 */
const refreshTokenProactively = async (): Promise<string | null> => {
  // Evitar múltiples llamadas de refresh simultáneas
  if (isRefreshing) {
    return new Promise((resolve) => {
      failedQueue.push({
        resolve: (token) => resolve(token || null),
        reject: () => resolve(null),
      });
    });
  }

  const refreshToken = TokenStorage.getRefreshToken();

  // Sesión sólo con cookies httpOnly: el navegador envía refresh en cookie pero JS no puede leerla.
  // POST /auth/refresh sin body: el backend usa migro_refresh_token desde cookie (BACK-001).
  if (!refreshToken) {
    isRefreshing = true;
    try {
      logger.debug('Refresh sin token en almacenamiento: intento con cookies httpOnly...');
      const response = await axios.post(
        `${config.API_BASE_URL}/auth/refresh`,
        undefined,
        { timeout: API_TIMEOUT, withCredentials: true }
      );
      const data = response.data;
      TokenStorage.saveTokens({
        access_token: data.access_token,
        refresh_token: data.refresh_token || '',
        token_type: data.token_type || 'bearer',
        expires_in: data.expires_in || 1209600,
        refresh_expires_in: data.refresh_expires_in || 2592000,
      });
      logger.debug('Token obtenido vía refresh con cookies httpOnly');
      processQueue(null, data.access_token);
      isRefreshing = false;
      return data.access_token;
    } catch (cookieRefreshError: unknown) {
      logger.debug(
        'Refresh con cookies httpOnly no disponible o falló (sin sesión o red)',
        cookieRefreshError
      );
      processQueue(cookieRefreshError, null);
      isRefreshing = false;
      return null;
    }
  }

  if (TokenStorage.isRefreshTokenExpired()) {
    logger.warn('Refresh token expirado');
    TokenStorage.clearTokens();
    return null;
  }

  isRefreshing = true;

  try {
    logger.debug('Refrescando token proactivamente...');
    const response = await axios.post(
      `${config.API_BASE_URL}/auth/refresh`,
      { refresh_token: refreshToken },
      { timeout: API_TIMEOUT, withCredentials: true }
    );

    const data = response.data;

    // Guardar nuevos tokens usando TokenStorage (usa expires_in del servidor)
    TokenStorage.saveTokens({
      access_token: data.access_token,
      refresh_token: data.refresh_token || refreshToken, // Usar el nuevo o mantener el anterior
      token_type: data.token_type || 'bearer',
      expires_in: data.expires_in || 1209600, // 14 días por defecto
      refresh_expires_in: data.refresh_expires_in || 2592000, // 30 días por defecto
    });

    logger.debug('Token refrescado exitosamente');

    // Procesar cola de peticiones en espera
    processQueue(null, data.access_token);
    isRefreshing = false;

    return data.access_token;
  } catch (refreshError: any) {
    logger.error('Error refrescando token:', refreshError);

    const refreshTokenExpired = TokenStorage.isRefreshTokenExpired();
    const noRefreshToken = !TokenStorage.getRefreshToken();

    const serverSaysTokenInvalid =
      (refreshError.response?.status === 400 &&
        (refreshError.response?.data?.detail?.toLowerCase().includes('token') ||
          refreshError.response?.data?.detail?.toLowerCase().includes('invalid') ||
          refreshError.response?.data?.detail?.toLowerCase().includes('expired'))) ||
      (refreshError.response?.status === 401 &&
        (refreshError.response?.data?.detail?.toLowerCase().includes('token') ||
          refreshError.response?.data?.detail?.toLowerCase().includes('invalid') ||
          refreshError.response?.data?.detail?.toLowerCase().includes('expired'))) ||
      (refreshError.response?.status === 403 &&
        (refreshError.response?.data?.detail?.toLowerCase().includes('token') ||
          refreshError.response?.data?.detail?.toLowerCase().includes('invalid') ||
          refreshError.response?.data?.detail?.toLowerCase().includes('expired')));

    const shouldClearTokens = refreshTokenExpired || noRefreshToken || serverSaysTokenInvalid;

    if (shouldClearTokens) {
      logger.warn('Limpiando tokens debido a refresh token inválido/expirado:', {
        refreshTokenExpired,
        noRefreshToken,
        serverSaysTokenInvalid,
        status: refreshError.response?.status,
        detail: refreshError.response?.data?.detail,
      });
      TokenStorage.clearTokens();
    } else {
      logger.warn('Error temporal al refrescar token, MANTENIENDO tokens:', {
        message: refreshError.message,
        status: refreshError.response?.status,
        code: refreshError.code,
        detail: refreshError.response?.data?.detail,
      });
    }

    processQueue(refreshError, null);
    isRefreshing = false;

    return null;
  }
};

/**
 * JWT para WebSocket de transcripción (?token=). No usa API key en la query string.
 * Si el CRM va sólo con VITE_API_BEARER_KEY, devuelve null.
 */
export async function ensureAccessTokenForWebSocket(): Promise<string | null> {
  const hasJwt = !!TokenStorage.getAccessToken();
  if (!hasJwt && config.API_BEARER_KEY) {
    return null;
  }
  // Sin JWT en memoria: refrescar con refresh en localStorage o sólo con cookies httpOnly (sesión F5 / CRM).
  if (!hasJwt) {
    const refreshed = await refreshTokenProactively();
    return refreshed ?? TokenStorage.getAccessToken();
  }
  if (TokenStorage.isTokenExpired()) {
    const refreshed = await refreshTokenProactively();
    if (refreshed) return refreshed;
  }
  return TokenStorage.getAccessToken();
}

// Función para configurar interceptores (se llama después de crear la instancia)
function setupApiInterceptors(instance: ReturnType<typeof axios.create>) {
  // Request interceptor - Add JWT token and check expiration proactively
  instance.interceptors.request.use(
  async (requestConfig) => {
    // Iniciar medición de performance para llamadas API
    if (requestConfig.url) {
      const markName = performanceTracingService.start(
        requestConfig.url,
        'api',
        {
          method: requestConfig.method?.toUpperCase(),
          url: requestConfig.url,
          endpoint: requestConfig.url?.replace(getApiInstance().defaults.baseURL || '', '') || '',
        }
      );
      (requestConfig as any).__perfMarkName = markName;
    }

    // No añadir token en endpoints de autenticación pública o health checks
    const publicEndpoints = [
      '/auth/login', 
      '/auth/otp/request',
      '/auth/otp/verify',
      '/auth/register', 
      '/auth/refresh',
      // Pili LLM deshabilitado - movido a repositorio externo
      // '/ai/pili-openai/health', // Health check no requiere autenticación
      '/pipelines/admin/approve-hiring-code' // Endpoint público de aprobación con token
    ];
    
    // Endpoints públicos de contratación (no requieren autenticación)
    // Rutas bajo /crm/hiring/ y /admin/hiring/ requieren JWT (CRM o admin)
    const isPublicHiringEndpoint = requestConfig.url?.includes('/hiring/') && 
                                    !requestConfig.url?.includes('/crm/hiring/') &&
                                    !requestConfig.url?.includes('/admin/hiring/');
    
    // GET anexos bajo CRM (/crm/hiring/.../annexes) requiere autenticación JWT (agent/lawyer/admin)
    const isPublicEndpoint = (requestConfig.url && publicEndpoints.some(endpoint => requestConfig.url!.includes(endpoint))) || 
                            isPublicHiringEndpoint;
    
    if (import.meta.env.DEV && requestConfig.url?.includes('/annexes')) {
      logger.debug('[api.ts] Endpoint de anexos:', {
        url: requestConfig.url,
        method: requestConfig.method,
        isPublicEndpoint,
      });
    }
    
    if (!isPublicEndpoint) {
      const jwtToken = TokenStorage.getAccessToken();
      // Fallback: API Key Bearer para Frontend CRM (p. ej. crm.migro.es) cuando no hay JWT
      let token: string | null = jwtToken;
      if (!token && config.API_BEARER_KEY) {
        token = config.API_BEARER_KEY;
      }
      const usingApiKey = !jwtToken && !!token && token === config.API_BEARER_KEY;
      
      if (import.meta.env.DEV && requestConfig.url?.includes('/annexes')) {
        logger.debug('[api.ts] Token para anexos:', {
          hasToken: !!token,
          tokenLength: token?.length,
        });
      }
      
      if (token) {
        // Lógica de expiración/refresh solo para JWT; la API Key no expira en cliente
        if (!usingApiKey) {
          // Usar TokenStorage como fuente de verdad (usa expires_in del servidor con buffer de 2 min)
          const tokenExpired = TokenStorage.isTokenExpired();
          let jwtExpired = false;
          if (tokenExpired) {
            jwtExpired = isTokenExpired(token);
          }
          if (tokenExpired && jwtExpired) {
            logger.warn('Token expirado (confirmado por TokenStorage y JWT), intentando refrescar...');
            const newToken = await refreshTokenProactively();
            if (newToken) {
              token = newToken;
            } else {
              const refreshToken = TokenStorage.getRefreshToken();
              if (!refreshToken || TokenStorage.isRefreshTokenExpired()) {
                if (window.location.pathname.startsWith('/admin') || 
                    window.location.pathname.startsWith('/crm') ||
                    window.location.pathname.startsWith('/contrato')) {
                  window.location.href = '/auth/login';
                }
              }
              return Promise.reject(new Error('Token expirado y no se pudo refrescar'));
            }
          } else if (tokenExpired && !jwtExpired) {
            logger.warn('TokenStorage indica expiración pero JWT es válido, refrescando preventivamente...');
            const newToken = await refreshTokenProactively();
            if (newToken) {
              token = newToken;
            }
          } else if (!tokenExpired && isTokenExpiringSoon(token, 2)) {
            const timeRemaining = getTokenTimeRemaining(token);
            if (timeRemaining !== null) {
              const minutesRemaining = Math.floor(timeRemaining / 60);
              const secondsRemaining = timeRemaining % 60;
              if (minutesRemaining > 0) {
                logger.debug(`Token expirará en ${minutesRemaining} min ${secondsRemaining} seg, refrescando proactivamente...`);
              } else {
                logger.debug(`Token expirará en ${secondsRemaining} segundos, refrescando proactivamente...`);
              }
            }
            const newToken = await refreshTokenProactively();
            if (newToken) {
              token = newToken;
            }
          }
        }
        
        requestConfig.headers.Authorization = `Bearer ${token}`;
        
        if (requestConfig.url?.includes('/annexes')) {
          if (import.meta.env.DEV) logger.debug('[api.ts] Token JWT agregado para anexos');
        }
      } else {
        // Log de depuración si no hay token
        if (requestConfig.url?.includes('/annexes')) {
          logger.warn('[api.ts] No hay token disponible para endpoint de anexos');
        }
      }
    } else {
      // Log de depuración si es endpoint público
      if (requestConfig.url?.includes('/annexes')) {
        if (import.meta.env.DEV) logger.debug('[api.ts] Endpoint de anexos es público, no se agrega token');
      }
    }
    
    if (import.meta.env.DEV && requestConfig.url?.includes('/crm/contacts') && requestConfig.params?.search) {
      logger.debug('[api.ts] Búsqueda de contactos:', {
        url: requestConfig.url,
        search: requestConfig.params.search,
        searchType: typeof requestConfig.params.search,
        searchLength: requestConfig.params.search.length,
        fullParams: requestConfig.params
      });
    }
    
    // IMPORTANTE: Si el body es FormData, eliminar Content-Type para que axios 
    // genere automáticamente el boundary correcto. Si se establece explícitamente
    // sin boundary, el servidor no puede parsear el cuerpo de la petición.
    if (requestConfig.data instanceof FormData) {
      // Eliminar Content-Type si está presente (axios lo establecerá automáticamente con boundary)
      if (requestConfig.headers && 'Content-Type' in requestConfig.headers) {
        delete requestConfig.headers['Content-Type'];
      }
      // También eliminar del objeto defaults.headers si está presente
      if (requestConfig.headers && 'content-type' in requestConfig.headers) {
        delete requestConfig.headers['content-type'];
      }
    }
    
    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

  // Response interceptor - Handle errors and refresh token
  instance.interceptors.response.use(
  (response) => {
    // Finalizar medición de performance
    const markName = (response.config as any).__perfMarkName;
    if (markName) {
      performanceTracingService.end(markName, 'success', {
        status: response.status,
        statusText: response.statusText,
      });
    }

    if (import.meta.env.DEV && import.meta.env.VITE_DEBUG === 'true') {
      logger.debug(`${response.config.method?.toUpperCase()} ${response.config.url} → ${response.status}`);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;
    
    // Finalizar medición de performance con error
    const markName = (originalRequest as any)?.__perfMarkName;
    if (markName) {
      performanceTracingService.end(markName, 'error', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        error: error.message,
      });
    }
    
    // Detailed error logging — skip noisy errors handled gracefully by callers
    const errStatus = error.response?.status;
    const errUrl = error.config?.url || '';
    // Skip: 404/409 (always), 500 on endpoints known to be unimplemented
    const isSkippableError =
      errStatus === 404 ||
      errStatus === 409 ||
      (errStatus === 500 && (errUrl.includes('/admin/agents/') || errUrl.includes('/admin/call-types')));
    if (!isSkippableError) {
      logger.error('API Error Details:', {
        url: errUrl,
        method: error.config?.method,
        status: errStatus,
        responseData: error.response?.data,
      });
      // Reportar 4xx/5xx al sink nginx para que aparezcan en logs de Render.
      // Excluimos 401 retried (el flujo de refresh ya genera ruido propio) y
      // los skippables conocidos (404/409 esperados, 500 en endpoints stub).
      const isRetried401 =
        errStatus === 401 && (originalRequest as any)?._retry === true;
      if (!isRetried401 && (errStatus === undefined || errStatus >= 400)) {
        reportClientError({
          type: 'http',
          message: error.message || `HTTP ${errStatus ?? 'error'}`,
          status: errStatus,
          url: errUrl,
          method: error.config?.method?.toUpperCase(),
        });
      }
    }
    
    if (error.response) {
      const { status, data } = error.response;
      // 400 con detail en formato { message, code }: usar message para que el usuario vea el motivo
      if (status === 400 && data && typeof data === 'object' && 'detail' in data) {
        const detail = (data as { detail?: unknown }).detail;
        const serverMessage =
          typeof detail === 'object' && detail != null && typeof (detail as { message?: string }).message === 'string'
            ? (detail as { message: string }).message
            : typeof detail === 'string'
              ? detail
              : null;
        if (serverMessage) {
          error.message = serverMessage;
        }
      }

      // Token expired or unauthorized - Intentar refresh token
      // IMPORTANTE: Solo intentar refresh si es un 401 y tenemos tokens válidos
      // NO limpiar tokens por errores 401 que no sean de autenticación (ej: permisos)
      if (status === 401 && originalRequest && !originalRequest._retry) {
        // Verificar si es una ruta pública del frontend
        const isPublicFrontendRoute = window.location.pathname === '/' ||
                             window.location.pathname.includes('/contratacion/') || 
                             window.location.pathname.includes('/hiring/') ||
                             window.location.pathname === '/expirado' ||
                             window.location.pathname === '/404' ||
                             window.location.pathname === '/privacidad' ||
                             window.location.pathname === '/privacy';
        
        // Verificar si es un endpoint público de la API
        const publicApiEndpoints = [
          '/auth/login',
          '/auth/otp/request',
          '/auth/otp/verify',
          '/auth/register',
          '/auth/refresh',
          '/hiring/',
          '/public/appointments',
        ]; // Pili deshabilitado
        const isPublicApiEndpoint = originalRequest.url && publicApiEndpoints.some(endpoint => originalRequest.url!.includes(endpoint));
        
        if (isPublicFrontendRoute || isPublicApiEndpoint) {
          // En rutas públicas, simplemente rechazar el error sin intentar refresh
          return Promise.reject(error);
        }
        
        // Verificar si tenemos tokens válidos antes de intentar refresh
        // Si no hay tokens o están expirados, rechazar directamente sin limpiar
        const hasValidRefreshToken = TokenStorage.hasTokens() && 
                                     TokenStorage.getRefreshToken() && 
                                     !TokenStorage.isRefreshTokenExpired();
        
        if (!hasValidRefreshToken) {
          // No hay refresh token válido, rechazar error sin limpiar (ya no hay nada que limpiar)
          logger.warn('No hay refresh token válido, rechazando request sin limpiar tokens');
          return Promise.reject(error);
        }
        
        // Marcar que este request ya se está reintentando
        originalRequest._retry = true;
        
        try {
          // Intentar refrescar el token usando la función reutilizable
          // Esta función maneja internamente el flag isRefreshing y la cola
          // IMPORTANTE: refreshTokenProactively() solo limpia tokens si el refresh token está realmente inválido
          const newToken = await refreshTokenProactively();
          
          if (newToken) {
            // Actualizar header de la petición original
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            
            // Reintentar petición original
            return instance(originalRequest);
          } else {
            // Refresh falló - verificar si los tokens todavía existen
            // Si existen, fue un error temporal y NO debemos limpiar ni redirigir
            // Si no existen, refreshTokenProactively() ya los limpió (error de autenticación real)
            const stillHasTokens = TokenStorage.hasTokens() && 
                                  TokenStorage.getRefreshToken() && 
                                  !TokenStorage.isRefreshTokenExpired();
            
            if (!stillHasTokens) {
              // Los tokens fueron limpiados por refreshTokenProactively() porque el refresh token está realmente inválido
              // Solo redirigir si estamos en rutas de admin
              if (window.location.pathname.startsWith('/admin') || 
                  window.location.pathname.startsWith('/crm') ||
                  window.location.pathname.startsWith('/contrato')) {
                window.location.href = '/auth/login';
              }
            } else {
              // Los tokens todavía existen, fue un error temporal (red, timeout, 500, etc.)
              // NO limpiar tokens, NO redirigir, solo rechazar el error para que el componente lo maneje
              logger.warn('Error temporal al refrescar token, MANTENIENDO tokens y sesión. Rechazando request.');
            }
            
            return Promise.reject(new Error('No se pudo refrescar el token'));
          }
        } catch (refreshError) {
          // Error al intentar refrescar - verificar si los tokens todavía existen
          // IMPORTANTE: NUNCA limpiar tokens aquí, solo verificar si refreshTokenProactively() los limpió
          const stillHasTokens = TokenStorage.hasTokens() && 
                                TokenStorage.getRefreshToken() && 
                                !TokenStorage.isRefreshTokenExpired();
          
          if (!stillHasTokens) {
            // Los tokens fueron limpiados por refreshTokenProactively() porque el refresh token está realmente inválido
            // Solo redirigir si estamos en rutas de admin
            if (window.location.pathname.startsWith('/admin') || 
                window.location.pathname.startsWith('/crm') ||
                window.location.pathname.startsWith('/contrato')) {
              window.location.href = '/auth/login';
            }
          } else {
            // Los tokens todavía existen, fue un error temporal (red, timeout, 500, etc.)
            // NO limpiar tokens, NO redirigir, solo rechazar el error
            logger.warn('Error temporal al refrescar token, MANTENIENDO tokens y sesión. Rechazando request.');
          }
          
          return Promise.reject(refreshError);
        }
      }
      
      // IMPORTANTE: Para TODOS los demás errores (403, 404, 422, 500, timeout, etc.)
      // NUNCA limpiar tokens ni redirigir - solo rechazar el error
      // Los tokens solo se limpian cuando el refresh token está realmente inválido/expirado
      
      // IMPORTANTE: Para TODOS los demás errores (403, 404, 422, 500, etc.)
      // NUNCA limpiar tokens ni redirigir - solo rechazar el error
      // Los tokens solo se limpian cuando el refresh token está realmente inválido/expirado
      
      // Forbidden (403) - Error de permisos, NO de autenticación
      // NO limpiar tokens, solo rechazar error
      if (status === 403) {
        logger.error('Acceso denegado (403) - MANTENIENDO tokens y sesión');
      }
      
      // Not found (404) - Recurso no encontrado (expected for some endpoints like sessions/current)
      // NO limpiar tokens, solo rechazar error silenciosamente
      
      // Validation error (422) - Error de validación
      // NO limpiar tokens, solo rechazar error
      if (status === 422) {
        logger.error('Error de validación (422) - MANTENIENDO tokens y sesión');
      }
      
      // Server error (500+) - Error del servidor
      // NO limpiar tokens, solo rechazar error
      if (status >= 500 && !isSkippableError) {
        logger.error('Error del servidor (500+) - MANTENIENDO tokens y sesión');
      }
    } else if (error.request) {
      // Error de red o timeout - NO limpiar tokens
      logger.error('No se recibió respuesta del servidor (timeout/red) - MANTENIENDO tokens y sesión', error.request);
    } else {
      // Error al configurar request - NO limpiar tokens
      logger.error('Error al configurar request - MANTENIENDO tokens y sesión:', error.message);
    }
    
    // IMPORTANTE: Rechazar el error sin limpiar tokens
    // Los tokens solo se limpian cuando el refresh token está realmente inválido/expirado
    return Promise.reject(error);
  }
  );
}
/** Detail en 400 puede ser objeto { message, code } o string (ej. 404) */
function getDetailMessage(data: { detail?: unknown } | undefined): string | null {
  const detail = data?.detail;
  if (typeof detail === 'object' && detail != null && typeof (detail as { message?: string }).message === 'string') {
    return (detail as { message: string }).message;
  }
  if (typeof detail === 'string') {
    return detail;
  }
  return null;
}

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ detail?: string | { message?: string; code?: string } }>;
    
    if (axiosError.response) {
      const { status, data } = axiosError.response;
      const serverMessage = getDetailMessage(data);
      if (serverMessage) {
        return serverMessage;
      }
      
      // Default messages by status code
      switch (status) {
        case 400:
          return 'Solicitud inválida. Por favor verifica los datos.';
        case 401:
          return 'Código de contratación inválido o sin permisos.';
        case 403:
          return 'No tienes permisos para realizar esta acción.';
        case 404:
          return 'Código de contratación no encontrado o ha expirado.';
        case 409:
          return 'Conflicto con el estado actual.';
        case 410:
          return 'Este enlace ha expirado.';
        case 422:
          return 'Los datos proporcionados son inválidos.';
        case 429:
          return 'Demasiadas solicitudes. Por favor espera un momento.';
      case 500:
        // Para errores 500, intentar extraer mensaje específico del backend
        if (data?.detail) {
          const detailStr = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
          
          // Detectar errores de Pydantic comunes
          if (detailStr.includes('is not fully defined')) {
            return 'Error de configuración en el servidor. Por favor, contacta al administrador.';
          }
          
          // Mostrar mensaje del backend si está disponible (limitado a 200 caracteres)
          return detailStr.length > 200 ? detailStr.substring(0, 200) + '...' : detailStr;
        }
        return 'Error del servidor. Por favor intenta más tarde.';
      case 503:
        // Error 503 generalmente indica servicio no disponible o en mantenimiento
        // Mostrar mensaje específico del backend si está disponible
        if (data?.detail) {
          const detailStr = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
          return detailStr;
        }
        return 'Servicio temporalmente no disponible.';
        default:
          return 'Ha ocurrido un error inesperado.';
      }
    }
    
    if (axiosError.request) {
      return 'No se pudo conectar con el servidor. Verifica tu conexión.';
    }
  }
  
  return 'Error desconocido. Por favor intenta nuevamente.';
}

